/**
 * An in-app replacement for `window.confirm()`: `await confirmDialog({...})`
 * resolves to true (confirmed) or false (cancelled, Escape, backdrop).
 *
 * The one `<ConfirmHost />` in the app shell renders the dialog. With no host
 * mounted — a component rendered alone in a unit test — it falls back to
 * `window.confirm`, so a test can still stub the answer.
 */

export interface ConfirmRequest {
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    /** `danger` styles the confirm button red, for overwriting or discarding. */
    tone?: 'default' | 'danger'
}

export interface PendingConfirm extends ConfirmRequest {
    resolve: (confirmed: boolean) => void
}

let pending: PendingConfirm | null = null
const listeners = new Set<() => void>()

function emit() {
    for (const listener of listeners) listener()
}

export function subscribeConfirm(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export function getPendingConfirm(): PendingConfirm | null {
    return pending
}

export function settleConfirm(confirmed: boolean) {
    const current = pending
    pending = null
    emit()
    current?.resolve(confirmed)
}

export function confirmDialog(request: ConfirmRequest): Promise<boolean> {
    if (listeners.size === 0) {
        return Promise.resolve(window.confirm(`${request.title}\n\n${request.message}`))
    }
    // A second request while one is open answers the first with "no".
    if (pending) settleConfirm(false)
    return new Promise<boolean>((resolve) => {
        pending = { ...request, resolve }
        emit()
    })
}
